import React, { useState } from 'react';
import { CarerLoginRequest } from '../../types/index';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { Smartphone, LogIn, Shield } from 'lucide-react';
import SmsVerificationInput from './SmsVerificationInput';
import { useToastContext, handleApiError } from '../../contexts/ToastContext';

interface CarerLoginPageProps {
  onLoginSuccess?: (token: string, carer: any) => void;
  onSwitchToRegister?: () => void;
}

const CarerLoginPage: React.FC<CarerLoginPageProps> = ({ 
  onLoginSuccess, 
  onSwitchToRegister 
}) => {
  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userCheckLoading, setUserCheckLoading] = useState(false);
  
  const { login } = useAuth();
  const toast = useToastContext();

  // 验证手机号格式
  const isValidPhoneNumber = (phone: string) => {
    return /^1[3-9]\d{9}$/.test(phone);
  };

  // 第一步：发送登录验证码（含用户存在性检查）
  const handleSendLoginSms = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidPhoneNumber(phoneNumber)) {
      setError('请输入正确的手机号码');
      toast.error('格式错误', '请输入正确的手机号码');
      return;
    }

    setUserCheckLoading(true);
    setError(null);

    try {
      // 先检查用户是否存在
      const userCheck = await authService.checkUserExists(phoneNumber);
      
      if (!userCheck.exists) {
        setError('该手机号未注册照护者账户');
        toast.error('用户未注册', '该手机号未注册，请先注册照护者账户', {
          action: {
            label: '立即注册',
            onClick: () => {
              if (onSwitchToRegister) {
                onSwitchToRegister();
              }
            }
          }
        });
        return;
      }

      // 用户存在，显示欢迎信息并发送验证码
      if (userCheck.userInfo) {
        toast.success('找到用户', `欢迎回来，${userCheck.userInfo.name}！正在发送登录验证码...`);
      }

      setLoading(true);
      await authService.sendSmsCode(phoneNumber, 'login');
      setStep('verify');
      toast.success('验证码已发送', `验证码已发送至 ${phoneNumber}，请查收`);
      console.log('✅ 登录验证码发送成功');
      
    } catch (err: any) {
      console.error('❌ 发送登录验证码失败:', err);
      
      const errorMessage = err.message || '发送验证码失败，请重试';
      setError(errorMessage);
      handleApiError(err, toast);
    } finally {
      setLoading(false);
      setUserCheckLoading(false);
    }
  };

  // 第二步：验证并登录
  const handleLogin = async (codeFromInput?: string) => {
    // 使用传入的验证码，如果没有则使用状态中的验证码
    const currentCode = codeFromInput || verificationCode;
    console.log('🔍 登录验证检查:', { 
      phoneNumber, 
      currentCode, 
      verificationCode, 
      length: currentCode?.length 
    });
    
    if (!currentCode || currentCode.length !== 6) {
      console.log('❌ 验证码格式检查失败:', { currentCode, verificationCode, length: currentCode?.length });
      setError('请输入6位验证码');
      toast.error('验证码格式不完整', '请输入6位验证码');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 先清除所有错误提示
      toast.clearToasts();
      
      console.log('🔐 开始调用AuthContext.login()...', { phoneNumber, currentCode });
      
      // 使用AuthContext的login方法，这样会自动更新React状态
      await login(phoneNumber, currentCode);
      
      console.log('✅ AuthContext.login() 调用成功');
      
      // 验证登录状态
      const authState = authService.getAuthState();
      console.log('📊 登录后认证状态:', {
        isAuthenticated: authState.isAuthenticated,
        hasToken: !!authState.token,
        carerName: authState.carer?.name
      });
      
      toast.success('登录成功', '欢迎进入ICOPE关怀助手！');
      console.log('✅ 照护者登录成功，准备触发成功回调');
      
      if (onLoginSuccess) {
        onLoginSuccess(authState.token || 'token', authState.carer || 'carer');
      }
    } catch (err: any) {
      console.error('❌ 登录失败详细信息:', {
        error: err,
        message: err.message,
        stack: err.stack,
        phoneNumber,
        codeLength: currentCode?.length
      });
      
      const errorMessage = err.message || '登录失败，请重试';
      setError(errorMessage);
      
      // 重置到手机号输入步骤，便于重新尝试
      if (err.message?.includes('验证码') || err.message?.includes('验证失败')) {
        console.log('🔄 验证码错误，保持在验证步骤');
        // 保持在验证步骤
      } else {
        console.log('🔄 登录失败，重置到手机号输入步骤');
        setStep('phone');
        setVerificationCode('');
      }
      
      handleApiError(err, toast);
    } finally {
      setLoading(false);
    }
  };

  // 重新发送验证码
  const handleResendLoginSms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await authService.sendSmsCode(phoneNumber, 'login');
      toast.success('验证码已重新发送', `新的验证码已发送至 ${phoneNumber}`);
      console.log('✅ 登录验证码重新发送成功');
    } catch (err: any) {
      console.error('❌ 重新发送登录验证码失败:', err);
      const errorMessage = err.message || '重新发送失败，请稍后重试';
      setError(errorMessage);
      handleApiError(err, toast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* 顶部Logo和标题 */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            老年人整合照护助手
          </h1>
          <p className="text-gray-600">
            用户登录 - 快速访问管理系统
          </p>
        </div>

        {/* 登录卡片 */}
        <div className="bg-white rounded-xl shadow-lg border p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* 第一步：输入手机号 */}
          {step === 'phone' && (
            <form onSubmit={handleSendLoginSms} className="space-y-4">
              <div className="text-center mb-6">
                <LogIn className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">用户登录</h3>
                <p className="text-gray-600 text-sm">输入注册手机号，快速登录</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  注册手机号 *
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  placeholder="138****1234"
                  maxLength={11}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || userCheckLoading || !isValidPhoneNumber(phoneNumber)}
                className="w-full flex items-center justify-center space-x-2 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
              >
                <Smartphone className="w-5 h-5" />
                <span>
                  {userCheckLoading ? '检查用户中...' : 
                   loading ? '发送中...' : 
                   '发送登录验证码'}
                </span>
              </button>

              {/* 切换到注册 */}
              <div className="text-center pt-4 border-t">
                <p className="text-gray-600 text-sm">
                  还没有账户？
                  <button
                    type="button"
                    onClick={onSwitchToRegister}
                    className="text-green-600 hover:text-green-700 font-medium ml-1"
                  >
                    立即注册
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* 第二步：验证登录验证码 */}
          {step === 'verify' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <LogIn className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">输入登录验证码</h3>
                <p className="text-gray-600 text-sm">
                  验证码已发送至 <span className="font-medium">{phoneNumber}</span>
                </p>
              </div>

              <SmsVerificationInput
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleLogin}
                loading={loading}
                onResend={handleResendLoginSms}
              />

              <button
                type="button"
                onClick={() => setStep('phone')}
                className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                返回修改手机号
              </button>
            </div>
          )}
        </div>

        {/* 安全提示 */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
          <p className="text-amber-800 text-sm">
            🔒 <strong>安全提示:</strong> 
            请勿将验证码告知他人，我们不会通过任何方式主动索要您的验证码
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarerLoginPage;